import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {type ScribeArtifact, useDeleteScribeArtifact} from '@github-ui/scribe-artifacts'
import {isStaff} from '@github-ui/stats'
import {ChevronDownIcon, ChevronRightIcon, FileIcon, LinkIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, CounterLabel, IconButton, Label} from '@primer/react'
import {clsx} from 'clsx'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import type {PanelArtifact} from './ArtifactPanelContent'
import {PersistedArtifactRow} from './ArtifactRow'
import {useArtifactsPanelActions} from './ArtifactsPanelContext'
import styles from './ArtifactsSection.module.css'
import type {EditableArtifact} from './create-artifact-dialog-types'
import {CreateArtifactDialog} from './CreateArtifactDialog'
import {CreateLinkDialog} from './CreateLinkDialog'
import {groupArtifactsByType} from './group-artifacts'
import {shouldShowArtifactsSection} from './issue-artifacts-visibility'
import type {IssueArtifact} from './types'
import {useCreateArtifactDialog} from './use-create-artifact-dialog'

const ARTIFACTS_STAFF_FEEDBACK_URL = 'https://gh.io/scribe-artifacts-feedback'

/** Maps a Scribe API artifact (snake_case payload) onto the section's view model. */
function mapScribeArtifact(artifact: ScribeArtifact, index: number): IssueArtifact {
  const numericId = typeof artifact.id === 'number' ? artifact.id : Number(artifact.id)
  return {
    id: Number.isFinite(numericId) ? numericId : index + 1,
    number: artifact.number ?? index + 1,
    type: artifact.type === 'plan' || artifact.type === 'link' ? artifact.type : 'generic',
    name: artifact.name,
    body: artifact.body ?? '',
    bodyHTML: artifact.body_html ?? undefined,
    actor: {login: artifact.creator?.login ?? '', avatarUrl: ''},
    createdAt: artifact.created_at ?? '',
    updatedAt: artifact.updated_at ?? '',
    updatedByActor: artifact.updated_by_actor ? {login: artifact.updated_by_actor.login} : undefined,
  }
}

type ArtifactsSectionContentProps = {
  resourceId: number
  artifacts: ScribeArtifact[]
  onSelectArtifact: (artifact: PanelArtifact) => void
  onArtifactDelete?: (id: number) => void
  onLastArtifactDeleted?: () => void
}

function ArtifactsSectionContent({
  resourceId,
  artifacts,
  onSelectArtifact,
  onArtifactDelete,
  onLastArtifactDeleted,
}: ArtifactsSectionContentProps) {
  const [editingArtifact, setEditingArtifact] = useState<{
    artifact: EditableArtifact
    returnFocusRef: RefObject<HTMLButtonElement | null>
  } | null>(null)
  const persistedArtifacts = useMemo<IssueArtifact[]>(
    () => groupArtifactsByType(artifacts.map(mapScribeArtifact)),
    [artifacts],
  )

  const {mutate: deleteArtifact} = useDeleteScribeArtifact({resourceType: 'issue', resourceId})

  const artifactNumbersById = useMemo(
    () => new Map(persistedArtifacts.map(artifact => [artifact.id, artifact.number])),
    [persistedArtifacts],
  )

  const totalArtifacts = persistedArtifacts.length

  const handleDelete = useCallback(
    (id: number) => {
      const number = artifactNumbersById.get(id)
      if (number === undefined) return
      deleteArtifact(
        {number},
        {
          onSuccess: () => {
            onArtifactDelete?.(id)
            if (totalArtifacts === 1) {
              onLastArtifactDeleted?.()
            }
          },
        },
      )
    },
    [artifactNumbersById, deleteArtifact, onArtifactDelete, onLastArtifactDeleted, totalArtifacts],
  )

  const handleEdit = useCallback((artifact: EditableArtifact, returnFocusRef: RefObject<HTMLButtonElement | null>) => {
    setEditingArtifact({artifact, returnFocusRef})
  }, [])

  const handleCloseEditDialog = useCallback(() => {
    setEditingArtifact(null)
  }, [])

  if (totalArtifacts === 0) {
    return null
  }

  return (
    <>
      <ul className={styles.List} aria-label="Artifacts">
        {persistedArtifacts.map(artifact => (
          <PersistedArtifactRow
            key={`${artifact.type}:${artifact.id}`}
            id={artifact.id}
            number={artifact.number}
            type={artifact.type}
            name={artifact.name}
            body={artifact.body}
            bodyHTML={artifact.bodyHTML}
            updatedAt={artifact.updatedAt}
            updatedByActor={artifact.updatedByActor}
            onSelect={onSelectArtifact}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        ))}
      </ul>
      {editingArtifact &&
        (editingArtifact.artifact.type === 'link' ? (
          <CreateLinkDialog
            resourceId={resourceId}
            artifact={editingArtifact.artifact}
            onClose={handleCloseEditDialog}
            returnFocusRef={editingArtifact.returnFocusRef}
          />
        ) : (
          <CreateArtifactDialog
            resourceId={resourceId}
            artifact={editingArtifact.artifact}
            onClose={handleCloseEditDialog}
            returnFocusRef={editingArtifact.returnFocusRef}
          />
        ))}
    </>
  )
}

type ArtifactsSectionProps = {
  resourceId: number
  artifacts: ScribeArtifact[]
  focusOnMount?: boolean
  /** True when no section (e.g. sub-issues) follows this card, so it meets the timeline flush. */
  isLastSection?: boolean
  onArtifactDelete?: (id: number) => void
  onFocusTargetReady?: () => void
  onLastArtifactDeleted?: () => void
}

export function ArtifactsSection({
  resourceId,
  artifacts,
  focusOnMount = false,
  isLastSection = false,
  onArtifactDelete,
  onFocusTargetReady,
  onLastArtifactDeleted,
}: ArtifactsSectionProps) {
  const [isExpanded, setIsExpanded] = useState<boolean | null>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  const {selectArtifact: handleSelectArtifact, removeArtifact} = useArtifactsPanelActions()

  const artifactCount = artifacts.length
  // Default expanded when artifacts exist, collapsed when empty. User toggle wins.
  const effectiveExpanded = isExpanded ?? artifactCount > 0

  useEffect(() => {
    if (!focusOnMount) return
    const toggleButton = toggleButtonRef.current
    if (!toggleButton) return
    toggleButton.focus()
    onFocusTargetReady?.()
  }, [focusOnMount, onFocusTargetReady])

  const toggleExpanded = useCallback(() => {
    setIsExpanded(expanded => !(expanded ?? artifactCount > 0))
  }, [artifactCount])

  const handleArtifactCreated = useCallback(() => {
    setIsExpanded(true)
  }, [])

  const handleArtifactDelete = useCallback(
    (id: number) => {
      const artifact = artifacts.map(mapScribeArtifact).find(item => item.id === id)
      if (artifact) {
        removeArtifact(`${artifact.type}:${artifact.id}`)
      }
      onArtifactDelete?.(id)
    },
    [artifacts, onArtifactDelete, removeArtifact],
  )

  const {
    createArtifactDialog,
    openCreateArtifactDialog: handleCreateArtifact,
    openCreateLinkDialog: handleCreateLink,
    triggerRef: addArtifactButtonRef,
  } = useCreateArtifactDialog({resourceId, onCreated: handleArtifactCreated})

  if (!shouldShowArtifactsSection(artifacts)) return null

  return (
    <div
      className={clsx(
        styles.Container,
        isFeatureEnabled('issue_inline_avatars') && styles.inlineAvatars,
        isLastSection && styles.lastSection,
      )}
      data-testid={TEST_IDS.issueArtifactsSection}
    >
      <div className={styles.Header}>
        <div className={styles.TitleGroup}>
          <IconButton
            ref={toggleButtonRef}
            className={styles.ToggleButton}
            icon={effectiveExpanded ? ChevronDownIcon : ChevronRightIcon}
            variant="invisible"
            size="small"
            aria-label={effectiveExpanded ? 'Collapse artifacts' : 'Expand artifacts'}
            aria-expanded={effectiveExpanded}
            onClick={toggleExpanded}
          />
          <span className={styles.TitleLabel}>Artifacts</span>
          {artifactCount > 0 && <CounterLabel>{artifactCount}</CounterLabel>}
          {isStaff() && (
            <a
              href={ARTIFACTS_STAFF_FEEDBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Send feedback about artifacts. Opens in a new tab."
              className={styles.StaffFeedbackLink}
            >
              <Label variant="accent">Staff</Label>
            </a>
          )}
        </div>
        <ActionMenu anchorRef={addArtifactButtonRef}>
          <ActionMenu.Anchor>
            <IconButton
              icon={PlusIcon}
              variant="invisible"
              size="small"
              aria-label="Add artifact"
              disabled={resourceId <= 0}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={handleCreateLink}>
                <ActionList.LeadingVisual>
                  <LinkIcon />
                </ActionList.LeadingVisual>
                Add link
              </ActionList.Item>
              <ActionList.Item onSelect={handleCreateArtifact}>
                <ActionList.LeadingVisual>
                  <FileIcon />
                </ActionList.LeadingVisual>
                Create artifact
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>

      <div
        className={clsx(styles.Body, !effectiveExpanded && styles.collapsed)}
        inert={!effectiveExpanded || undefined}
      >
        <div className={styles.BodyInner}>
          <ErrorBoundary fallback={null} critical={false} boundaryName="ArtifactsSection">
            <ArtifactsSectionContent
              resourceId={resourceId}
              artifacts={artifacts}
              onSelectArtifact={handleSelectArtifact}
              onArtifactDelete={handleArtifactDelete}
              onLastArtifactDeleted={onLastArtifactDeleted}
            />
          </ErrorBoundary>
        </div>
      </div>

      {createArtifactDialog}
    </div>
  )
}
